/* Lux UGC Gallery — video controller.

   Responsibilities:
   • Lazy-attach <source> URLs and play muted loops only while a tile is on screen.
   • Fall back silently to the poster frame when autoplay is refused (iOS Low Power
     Mode, data saver, reduced-motion preference).
   • Swap YouTube / Vimeo facades for a real iframe on click — never before.
   • Freeze the desktop marquee while any video is actively playing with intent.
   • Keep sound exclusive: unmuting one tile mutes every other one.

   Deliberately dependency-free and delegated, so Theme Editor section reloads
   don't require re-binding individual listeners. */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var observer = null;

  /* ---------- helpers ---------- */

  function mediaOf(el) {
    return el.closest('.lux-ugc__media');
  }

  function tileOf(el) {
    return el.closest('.lux-ugc__tile');
  }

  function videoOf(el) {
    var tile = tileOf(el);
    return tile ? tile.querySelector('.lux-ugc__video') : null;
  }

  /* The marquee is paused via a class rather than inline styles so the CSS
     keeps ownership of animation state (hover and focus-within still work). */
  function setMarqueeHold(el, on) {
    var grid = el.closest('.lux-ugc__grid');
    if (!grid) return;
    if (on) {
      grid.dataset.luxHold = String((parseInt(grid.dataset.luxHold, 10) || 0) + 1);
    } else {
      var next = (parseInt(grid.dataset.luxHold, 10) || 0) - 1;
      grid.dataset.luxHold = String(Math.max(0, next));
    }
    grid.classList.toggle('is-video-active', (parseInt(grid.dataset.luxHold, 10) || 0) > 0);
  }

  function attachSources(video) {
    if (video.dataset.luxLoaded === 'true') return;
    video.dataset.luxLoaded = 'true';
    var sources = video.querySelectorAll('source[data-src]');
    for (var i = 0; i < sources.length; i++) {
      sources[i].src = sources[i].getAttribute('data-src');
    }
    video.load();
  }

  function markPlaying(video) {
    var media = mediaOf(video);
    if (media) media.classList.add('is-playing');
  }

  /* play() returns a promise that rejects when the browser refuses autoplay.
     Swallowing it leaves the poster frame in place, which is the correct
     visual fallback — never surface an error to the shopper. */
  function tryPlay(video) {
    attachSources(video);
    var promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.then(function () {
        markPlaying(video);
      }).catch(function () {
        /* poster stays visible */
      });
    } else {
      markPlaying(video);
    }
  }

  function resetSoundButton(tile) {
    var button = tile ? tile.querySelector('[data-lux-ugc-sound]') : null;
    if (!button) return;
    button.setAttribute('aria-pressed', 'false');
    button.classList.remove('is-on');
  }

  function muteAll(except) {
    var videos = document.querySelectorAll('.lux-ugc__video');
    for (var i = 0; i < videos.length; i++) {
      var video = videos[i];
      if (video === except) continue;
      if (!video.muted) {
        video.muted = true;
        setMarqueeHold(video, false);
        resetSoundButton(tileOf(video));
      }
    }
  }

  /* ---------- viewport-driven playback ---------- */

  function handleIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      var video = entries[i].target;

      if (entries[i].isIntersecting) {
        if (video.dataset.autoplay === 'true' && !REDUCED.matches) tryPlay(video);
        continue;
      }

      /* Left the viewport: stop everything and hand sound back. */
      if (!video.paused) video.pause();
      if (!video.muted) {
        video.muted = true;
        setMarqueeHold(video, false);
        resetSoundButton(tileOf(video));
      }
      if (video.dataset.userStarted === 'true') {
        video.dataset.userStarted = 'false';
        video.controls = false;
        setMarqueeHold(video, false);
        var tile = tileOf(video);
        var play = tile ? tile.querySelector('[data-lux-ugc-play]') : null;
        if (play) play.classList.remove('is-hidden');
        var media = mediaOf(video);
        if (media) media.classList.remove('is-playing', 'is-interactive');
      }
    }
  }

  function observe() {
    if (!('IntersectionObserver' in window)) return;

    if (!observer) {
      observer = new IntersectionObserver(handleIntersect, {
        rootMargin: '200px 0px',
        threshold: 0.25
      });
    }

    var videos = document.querySelectorAll('.lux-ugc__video:not([data-lux-observed])');
    for (var i = 0; i < videos.length; i++) {
      videos[i].setAttribute('data-lux-observed', 'true');
      observer.observe(videos[i]);
    }
  }

  /* ---------- interactions ---------- */

  function playFacade(button) {
    var tile = tileOf(button);
    var media = tile ? tile.querySelector('.lux-ugc__media') : null;
    if (!media) return;

    button.classList.add('is-hidden');

    if (button.getAttribute('data-video-kind') === 'external') {
      var src = button.getAttribute('data-embed-src');
      if (!src) return;

      var iframe = document.createElement('iframe');
      iframe.className = 'lux-ugc__iframe';
      iframe.src = src;
      iframe.title = button.getAttribute('aria-label') || 'Video';
      iframe.loading = 'lazy';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('frameborder', '0');

      media.appendChild(iframe);
      media.classList.add('is-playing', 'is-interactive');
      setMarqueeHold(media, true);
      return;
    }

    var video = tile.querySelector('.lux-ugc__video');
    if (!video) return;

    video.dataset.userStarted = 'true';
    video.controls = true;
    video.muted = false;
    media.classList.add('is-interactive');
    muteAll(video);
    setMarqueeHold(video, true);
    tryPlay(video);
  }

  function toggleSound(button) {
    var video = videoOf(button);
    if (!video) return;

    var willUnmute = video.muted;

    if (willUnmute) {
      muteAll(video);
      video.muted = false;
      setMarqueeHold(video, true);
      /* A muted loop may already be running; make sure it is actually playing
         before the shopper hears nothing and assumes the toggle is broken. */
      if (video.paused) tryPlay(video);
    } else {
      video.muted = true;
      setMarqueeHold(video, false);
    }

    button.setAttribute('aria-pressed', willUnmute ? 'true' : 'false');
    button.classList.toggle('is-on', willUnmute);
  }

  document.addEventListener('click', function (event) {
    var play = event.target.closest('[data-lux-ugc-play]');
    if (play) {
      event.preventDefault();
      playFacade(play);
      return;
    }

    var sound = event.target.closest('[data-lux-ugc-sound]');
    if (sound) {
      event.preventDefault();
      toggleSound(sound);
    }
  });

  /* Background tabs: stop decoding entirely. */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      var videos = document.querySelectorAll('.lux-ugc__video');
      for (var i = 0; i < videos.length; i++) {
        if (!videos[i].paused) videos[i].pause();
      }
    }
  });

  /* Reduced-motion can be toggled mid-session on macOS and iOS. */
  if (typeof REDUCED.addEventListener === 'function') {
    REDUCED.addEventListener('change', function () {
      if (!REDUCED.matches) return;
      var videos = document.querySelectorAll('.lux-ugc__video[data-autoplay="true"]');
      for (var i = 0; i < videos.length; i++) {
        if (videos[i].dataset.userStarted !== 'true') videos[i].pause();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }

  document.addEventListener('shopify:section:load', observe);
  document.addEventListener('shopify:section:unload', function (event) {
    var videos = event.target.querySelectorAll('.lux-ugc__video');
    for (var i = 0; i < videos.length; i++) {
      videos[i].pause();
      if (observer) observer.unobserve(videos[i]);
    }
  });
})();